/**
 * The reaction GIFs' page, `/chinese-meme-reaction-gifs/`.
 *
 * **Each picture is a still with a muted loop over it,** and the loop plays
 * only while its card is in view. `layouts/partials/biaoqing-writing.html`
 * draws the `<video>` with no source and `preload="none"`, so a card loads
 * its loop once it nears the screen, and a card in a closed section or behind
 * the script switch never does. The still shows until the loop is playing,
 * and stays for a reader who asks for less motion.
 *
 * **A link to a card opens its section.** The cards sit in `<details>` closed
 * until a reader opens them, and a link such as `#hahaha` from a post would
 * otherwise land on a closed section with the card hidden inside it. Chrome
 * opens it unasked and the other browsers did not when this was written.
 *
 * **A card starts in the writing the reader chose.** The page is the same
 * bytes for everybody, with every switch on 简体, and a reader whose settings
 * say Taiwan or Hong Kong has each switch turned to 繁體 as the page starts,
 * and the birthday GIF link's still (`data-biaoqing-fanti`) with them.
 * The head script has put that choice on `<html>` before this runs.
 */

import { chosenSettings } from "./reader-choice.js";

/** Just enough of an `IntersectionObserver` for the loops. */
export interface LoopWatcher {
  observe(target: Element): void;
}

/** What the page needs from the browser, the window's own unless given. */
export interface BiaoqingbaoBrowser {
  readonly reducedMotion: boolean;
  readonly watch: (
    seen: (target: Element, inView: boolean) => void,
  ) => LoopWatcher;
  readonly hash: () => string;
  readonly onHashChange: (listener: () => void) => void;
}

/** How far ahead of the screen a loop starts loading. */
const loadAhead = "200px";

/**
 * Start the page's loops and open the section a link names.
 * @param root - The page.
 * @param browser - The browser's motion preference, observer and address.
 */
export function startBiaoqingbao(
  root: Document,
  browser: BiaoqingbaoBrowser = windowBrowser(),
): void {
  if (root.querySelector(".biaoqingbao-section") === null) {
    return;
  }

  startInChosenWriting(root);
  playInView(root, browser);
  openNamed(root, browser.hash());
  browser.onHashChange(() => {
    openNamed(root, browser.hash());
  });
}

function startInChosenWriting(root: Document): void {
  if (chosenSettings(root).writing === "zh-CN") {
    return;
  }

  for (const fanti of root.querySelectorAll<HTMLInputElement>(
    '.biaoqing-switch input[value="fanti"]',
  )) {
    fanti.checked = true;
  }

  for (const still of root.querySelectorAll<HTMLImageElement>(
    "img[data-biaoqing-fanti]",
  )) {
    still.src = still.dataset["biaoqingFanti"] ?? still.src;
  }
}

function playInView(root: Document, browser: BiaoqingbaoBrowser): void {
  const loops = root.querySelectorAll<HTMLVideoElement>(
    "video[data-biaoqing-loop]",
  );

  if (browser.reducedMotion || loops.length === 0) {
    return;
  }

  const watcher = browser.watch((target, inView) => {
    const loop = target as HTMLVideoElement;

    if (!inView) {
      loop.pause();
      return;
    }

    if (loop.getAttribute("src") === null) {
      loop.src = loop.dataset["biaoqingLoop"] ?? "";
    }

    // A browser that refuses to play a muted loop leaves the still, which
    // is all the picture is without one.
    loop.play().catch(() => undefined);
  });

  for (const loop of loops) {
    loop.addEventListener(
      "playing",
      () => {
        loop.closest(".biaoqing-picture")?.classList.add("biaoqing-playing");
      },
      { once: true },
    );
    watcher.observe(loop);
  }
}

/**
 * Open the closed sections around the element a fragment names, and bring it
 * into view. The browser's own jump landed while the section was closed, and
 * Chrome, which opens the section itself, leaves the page at the top.
 */
function openNamed(root: Document, hash: string): void {
  const id = decodeURIComponent(hash.replace(/^#/u, ""));
  const named = id === "" ? null : root.getElementById(id);
  const inside = named?.closest("details");

  if (named === null || inside === null || inside === undefined) {
    return;
  }

  for (
    let section: HTMLDetailsElement | null = inside;
    section !== null;
    section = section.parentElement?.closest("details") ?? null
  ) {
    section.open = true;
  }

  named.scrollIntoView({ block: "start" });
}

function windowBrowser(): BiaoqingbaoBrowser {
  return {
    reducedMotion: matchMedia("(prefers-reduced-motion: reduce)").matches,
    watch: (seen) =>
      new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            seen(entry.target, entry.isIntersecting);
          }
        },
        { rootMargin: loadAhead },
      ),
    hash: () => location.hash,
    onHashChange: (listener) => {
      addEventListener("hashchange", listener);
    },
  };
}
